import { ReactNode, useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiAccountGroup, mdiCalendar, mdiCalendarMultiselect, mdiMapMarkerMultiple, mdiTimerSandComplete, mdiVote, mdiPlus } from "@mdi/js";
import { Link, useRouter } from "easy-page-router/react";

import { Footer } from "../Layout";
import { ThemeSwitch } from "../components/Theme";
import { LangSwitch, useSelectedLang } from "../components/Lang";
import { useHead } from "../services/common";
import { useLang, dictionary } from "../services/lang";
import Clock from "../components/Clock";
import Dialog from "../components/Dialog";
import { Page } from "../components";


export default function Item() {
    const { push, state } = useRouter();
    const { lang, dict } = useLang();
    const selectedLang = useSelectedLang();

    useHead({
        title: dict.seo.index.title,
        description: dict.seo.index.description,
    });

    // redirect to selected language
    useEffect(() => {
        if (state === "active" && selectedLang && selectedLang !== lang)
            push(`/${dictionary[selectedLang].url}`);
    }, [state, lang, selectedLang]);

    const [wordIndex, setWordIndex] = useState(0);
    const [showWizard, setShowWizard] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setWordIndex((wordIndex + 1) % 5), 5000);
        return () => clearTimeout(timeout);
    }, [wordIndex]);

    return <Page fixed={<>
        <header>
            <Clock size={32} className="logo" />
            <div className="grow-1" />
            <LangSwitch />
            <ThemeSwitch />
        </header>
        <Footer />
    </>}>

        <h1 className="titimeme"><span>ti</span><b>time</b><span>me</span><span className="com">.com</span><span className="hidden">: {dict.home.subTitle}</span></h1>

        <p className="page-description">{dict.home.text}</p>

        <div className="titimeme-words">
            {dict.home.words.map((word, index) => <div
                key={index}
                className={wordIndex === index ? "active" : ""}
                onMouseEnter={() => setWordIndex(index)}
            >
                <span className="word">{word}</span><span className="shadow">{word}</span>
            </div>)}
        </div>
        <p className="titimeme-words-text">
            {dict.home.wordsText[wordIndex]}
        </p>

        <div className="wizard-cta-container">
            <button className="wizard-cta-button" onClick={() => setShowWizard(true)}>
                <Icon path={mdiPlus} size={1.1} />
                <span>{dict.wizard.cta}</span>
            </button>
        </div>

        <div className="tools grid">
            <Tool type="countdown" title={dict.countdown} subTitle={dict.countdownSubTitle} description={dict.countdownDescription} href={`/${dict.urlCountdown}`} icon={mdiTimerSandComplete} />
            <Tool type="poll" title={dict.what} subTitle={dict.whatSubTitle} description={dict.whatDescription} href={`/${dict.urlPoll}`} icon={mdiVote} />
            <Tool type="who" title={dict.who} subTitle={dict.whoSubTitle} description={dict.whoDescription} href={`/${dict.urlWho}`} icon={mdiAccountGroup} />
            <Tool type="when" title={dict.when} subTitle={dict.whenSubTitle} description={dict.whenDescription} href={`/${dict.urlWhen}`} icon={mdiCalendar} />
            <Tool type="team" title={dict.team} subTitle={dict.teamSubTitle} description={dict.teamDescription} href={`/${dict.urlTeam}`} icon={mdiCalendarMultiselect} />
            <Tool type="where" title={dict.where} subTitle={dict.whereSubTitle} description={dict.whereDescription} icon={mdiMapMarkerMultiple} />
        </div>

        <Dialog show={showWizard} onClose={() => setShowWizard(false)} width={600}>
            <div className="wizard-dialog">
                <h2>{dict.wizard.title}</h2>
                <p className="wizard-subtitle">{dict.wizard.subTitle}</p>
                <div className="wizard-options">
                    <Link className="wizard-option option-countdown" href={`/${dict.urlCountdown}/${dict.urlNew}`} onClick={() => setShowWizard(false)}>
                        <div className="wizard-icon-wrapper">
                            <Icon path={mdiTimerSandComplete} size={1.3} />
                        </div>
                        <div className="wizard-option-content">
                            <h3>{dict.wizard.countdownTitle}</h3>
                            <p>{dict.wizard.countdownDesc}</p>
                        </div>
                    </Link>
                    <Link className="wizard-option option-when" href={`/${dict.urlWhen}/${dict.urlNew}`} onClick={() => setShowWizard(false)}>
                        <div className="wizard-icon-wrapper">
                            <Icon path={mdiCalendar} size={1.3} />
                        </div>
                        <div className="wizard-option-content">
                            <h3>{dict.wizard.whenTitle}</h3>
                            <p>{dict.wizard.whenDesc}</p>
                        </div>
                    </Link>
                    <Link className="wizard-option option-who" href={`/${dict.urlWho}/${dict.urlNew}`} onClick={() => setShowWizard(false)}>
                        <div className="wizard-icon-wrapper">
                            <Icon path={mdiAccountGroup} size={1.3} />
                        </div>
                        <div className="wizard-option-content">
                            <h3>{dict.wizard.whoTitle}</h3>
                            <p>{dict.wizard.whoDesc}</p>
                        </div>
                    </Link>
                    <Link className="wizard-option option-poll" href={`/${dict.urlPoll}/${dict.urlNew}`} onClick={() => setShowWizard(false)}>
                        <div className="wizard-icon-wrapper">
                            <Icon path={mdiVote} size={1.3} />
                        </div>
                        <div className="wizard-option-content">
                            <h3>{dict.wizard.pollTitle}</h3>
                            <p>{dict.wizard.pollDesc}</p>
                        </div>
                    </Link>
                    <Link className="wizard-option option-team" href={`/${dict.urlTeam}`} onClick={() => setShowWizard(false)}>
                        <div className="wizard-icon-wrapper">
                            <Icon path={mdiCalendarMultiselect} size={1.3} />
                        </div>
                        <div className="wizard-option-content">
                            <h3>{dict.wizard.teamTitle}</h3>
                            <p>{dict.wizard.teamDesc}</p>
                        </div>
                    </Link>
                </div>
            </div>
        </Dialog>
    </Page>;
}

type ToolProps = {
    title: string;
    subTitle?: string,
    description?: string;
    icon?: string;
    href?: string;
    type: "countdown" | "poll" | "who" | "when" | "team" | "where";
    children?: ReactNode;
}

function Tool({ title, subTitle, description, icon, href, type, children }: ToolProps) {
    const { dict } = useLang();

    const tool = <>
        <div className="tool-header">
            <h2>{title}{subTitle && <span className="sub-title">{subTitle}</span>}</h2>
            {icon && (
                <div className="tool-icon-wrapper">
                    <Icon className="icon" path={icon} size={1.5} />
                </div>
            )}
        </div>
        {description && <p className="tool-description">{description}</p>}
        {!href && <p className="preparing">{dict.preparing}</p>}
        {children}
    </>;

    if (href) {
        return <Link className={`grid-item tool tool-${type}`} href={href}>{tool}</Link>;
    } else {
        return <div className={`grid-item disabled tool tool-${type}`}>
            {tool}
        </div>;
    }
}

/* Version with examples
import { CountdownTime } from "./countdown/Countdown";
import { Votes } from "../components/Vote";
import { When } from "../components";

const date = new Date();
const today = date.getDay();
const daysUntilSaturday = (6 - today + 7) % 7;
date.setDate(date.getDate() + daysUntilSaturday);
date.setHours(0, 0, 0, 0);

const when1 = new Date();
when1.setHours(0, 0, 0, 0);
const when2 = new Date(when1);
when2.setDate(when1.getDate() + 1);
const when3 = new Date(when1);
when3.setDate(when1.getDate() + 2);

        <div className="tools grid">
            <Tool title={dict.whenCountdown + " - Odpočet času"} href={`/${dict.urlCountdown}`} >
                <h3 className="line-clamp-2">Víkend začíná za:</h3>
                <CountdownTime date={date} />
            </Tool>
            <Tool title={dict.what + " - Hlasování a ankety"} href={`/${dict.urlPoll}`} >
                <h3 className="line-clamp-2">Kam dnes na oběd?</h3>
                <ul className="poll-options">
                    <li className={`poll-option order-1`}><div className="accordion-header">5x Pizza u Karla</div></li>
                    <li className={`poll-option order-2`}><div className="accordion-header">3x Asijské bistro Panda</div></li>
                    <li className={`poll-option order-3`}><div className="accordion-header">2x Bageterie na rohu</div></li>
                </ul>
            </Tool>
            <Tool title={dict.who + " - Účast nebo Souhlas"} href={`/${dict.urlWho}`} >
                <h3 className="line-clamp-2">Kdo přijde na moje narozeniny?</h3>
                <div className="who-votes">
                    <div className="who-vote vote-yes">12x</div>
                    <div className="who-vote vote-maybe">3x</div>
                    <div className="who-vote vote-no">1x</div>
                </div>
            </Tool>
            <Tool title={dict.when + " - Hledání termínu"} href={`/${dict.urlWhen}`} >
                <h3 className="line-clamp-2">Termín příští porady?</h3>
                <ul className="when-options">
                    <li className={`when-option order-1`}>
                        <div className="accordion-header">
                            <Votes isMaybeOption votes={{ yes: 12, maybe: 3, no: 1 }} />
                            <span style={{ display: "inline-block", width: 10 }} />
                            <When date={when1.toISOString()} />
                        </div>
                    </li>
                    <li className={`when-option order-1`}>
                        <div className="accordion-header">
                            <Votes isMaybeOption votes={{ yes: 6, maybe: 6, no: 4 }} />
                            <span style={{ display: "inline-block", width: 10 }} />
                            <When date={when2.toISOString()} />
                        </div>
                    </li>
                    <li className={`when-option order-1`}>
                        <div className="accordion-header">
                            <Votes isMaybeOption votes={{ yes: 4, maybe: 5, no: 7 }} />
                            <span style={{ display: "inline-block", width: 10 }} />
                            <When date={when3.toISOString()} />
                        </div>
                    </li>
                </ul>
            </Tool>
            <Tool title={dict.where + " - Lokalita"} description={"Brzy budete moci snadno hlasovat i o nejlepší lokalitě pro vaši akci."} icon={mdiMapMarkerMultiple} />
            <Tool title={dict.organization} description={" Nástroj pro efektivní organizaci pravidelných tréninků, zkoušek nebo jiných skupinových aktivit."} icon={mdiCalendarMultiselect} />
        </div>
*/